// src/pages/LeadIntake.js
// PUBLIC lead intake form — no sign-in required.
// Anyone with the link can submit a lead. They identify themselves as
// the "Lead Agent" (name + phone + optional email) so the submission
// is attributable, time/date stamped, and lands in the HomeIQa pipeline.
//
// Required: Lead Agent name + phone, listing URL, property address,
// owner phone. Optional: beds, baths, sqft, owner name, tipster email.
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../firebase';
import AddressAutocomplete from '../components/agent/AddressAutocomplete';

const BURG = '#6B1E2E';
const BURG_DK = '#4A1220';
const GOLD = '#C9A84C';
const GOLD_PALE = '#F7F0DC';
const CREAM = '#FAF7F2';
const BORDER = 'rgba(107,30,46,0.18)';
const MID = '#5A5A5A';
const LIGHT = '#9A9A9A';

export default function LeadIntake() {
  const { slug } = useParams();
  // If the URL has a slug, look up the matching Lead Agent. Their info
  // pre-fills the form and the "Lead Agent" section is hidden — every
  // submission via this URL is credited to them.
  const [pinned, setPinned] = useState(null); // { name, slug, role } or null
  const [pinLoading, setPinLoading] = useState(!!slug);
  const [pinError, setPinError] = useState('');

  useEffect(() => {
    if (!slug) { setPinLoading(false); return; }
    (async () => {
      try {
        const q = query(collection(db, 'agents'), where('intakeSlug', '==', slug), limit(1));
        const snap = await getDocs(q);
        if (snap.empty) {
          setPinError(`This share link ("${slug}") doesn't match any Lead Agent on record. Ask your admin to send you the current link — they can grab it from Settings → Active team → 🔗 Copy link.`);
        } else {
          const d = snap.docs[0].data();
          if (d.status !== 'active') {
            setPinError('Your Lead Agent account is paused right now. Please contact your admin.');
          } else {
            setPinned({
              name: d.name || d.email,
              slug,
              role: d.role
            });
          }
        }
      } catch (e) {
        setPinError(`Couldn't load that share link: ${e.message}`);
      } finally {
        setPinLoading(false);
      }
    })();
  }, [slug]);

  const [agent, setAgent] = useState({ name: '', phone: '', email: '' });
  const [address, setAddress] = useState({ line1: '', city: '', state: '', zip: '' });
  const [listingUrl, setListingUrl] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [beds, setBeds] = useState('');
  const [baths, setBaths] = useState('');
  const [sqft, setSqft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState(null); // { leadId, submittedAt }
  const [duplicate, setDuplicate] = useState(null);
  const formRef = useRef(null);

  // Focus the first field on mount.
  useEffect(() => {
    if (formRef.current) {
      const firstInput = formRef.current.querySelector('input, textarea');
      firstInput?.focus();
    }
  }, []);

  const fmtPhone = (v) => {
    const d = (v || '').replace(/\D/g, '').slice(0, 10);
    if (d.length < 4) return d;
    if (d.length < 7) return `(${d.slice(0,3)}) ${d.slice(3)}`;
    return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  };

  const validate = () => {
    // When the URL is pinned to a specific Lead Agent (Nicholas's
    // share link, etc.), their identity is already on the record —
    // they never type their own info. Otherwise (generic public
    // form), we require name + email + phone so attribution sticks.
    if (!pinned) {
      if (!agent.name.trim()) return 'Your name is required so we know who submitted this lead.';
      if (!agent.email.trim()) return 'Your email is required so we can keep you posted on the lead.';
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(agent.email.trim())) return 'That email doesn\'t look right.';
      if (!agent.phone.trim()) return 'Your phone number is required for follow-up.';
    }
    if (!listingUrl.trim()) return 'Listing URL is required.';
    try { new URL(listingUrl.trim()); } catch { return 'That listing URL doesn\'t look right — paste the full link from your browser.'; }
    if (!address.line1.trim()) return 'Property street address is required.';
    if (!ownerPhone.trim()) return 'Owner phone is required.';
    return '';
  };

  const submit = async (override = false) => {
    if (success) return;
    const v = validate();
    if (v) { setErr(v); return; }
    setErr(''); setSubmitting(true);
    try {
      const url = 'https://us-central1-homeiqa-2026.cloudfunctions.net/createPublicLead';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          override,
          slug: pinned ? pinned.slug : null,
          agent: pinned ? null : {
            name: agent.name.trim(),
            phone: agent.phone.trim(),
            email: agent.email.trim() || null
          },
          listingUrl: listingUrl.trim(),
          address: {
            line1: address.line1.trim(),
            city: address.city.trim(),
            state: (address.state || '').toUpperCase().slice(0, 2),
            zip: address.zip.trim()
          },
          owner: {
            name: ownerName.trim() || null,
            phone: ownerPhone.trim()
          },
          propertyData: {
            beds: beds ? Number(beds) : null,
            baths: baths ? Number(baths) : null,
            sqft: sqft ? Number(sqft) : null
          }
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data?.duplicate) {
          setDuplicate(data.duplicate);
          return;
        }
        setErr(data?.message || `Submission failed (HTTP ${res.status}).`);
        return;
      }
      setDuplicate(null);
      setSuccess({ leadId: data.id, submittedAt: data.submittedAt || Date.now() });
    } catch (e) {
      setErr(`Couldn't submit: ${e.message}. Check your internet and try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    const ts = new Date(success.submittedAt);
    return (
      <div style={s.shell}>
        <div style={s.successCard}>
          <div style={s.successIcon}>✓</div>
          <h1 style={s.successTitle}>Lead submitted, thank you!</h1>
          <p style={s.successBody}>
            We've added the property to the HomeIQa pipeline. The team will reach out to the seller and you'll be credited as the <strong>Lead Agent</strong> on this deal.
          </p>
          <div style={s.successMeta}>
            <div><strong>Lead Agent:</strong> {pinned ? pinned.name : agent.name}</div>
            <div><strong>Submitted:</strong> {ts.toLocaleString()}</div>
            <div><strong>Property:</strong> {address.line1}, {address.city}, {address.state} {address.zip}</div>
          </div>
          <button onClick={() => window.location.reload()} style={s.again}>+ Submit another lead</button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.shell}>
      <div style={s.wrap}>
        <header style={s.header}>
          <div style={s.brand}>
            Home<span style={{ color: GOLD }}>IQ</span><sup style={{ color: GOLD, fontStyle: 'italic', fontSize: 13 }}>a</sup>
          </div>
          <div style={s.eyebrow}>Lead Agent intake</div>
          <h1 style={s.title}>Submit a property tip</h1>
          <p style={s.sub}>
            Found a distressed house, FSBO, or motivated seller? Drop the details below.
            Our team takes it from there — and you're credited as the Lead Agent on every lead you submit.
          </p>
        </header>

        {/* Identity strip — small full-width bar showing who's logged in
            via the share link. Stays right under the header so it's
            never lost in the middle of the page. */}
        {pinned && (
          <div style={s.pinnedStrip}>
            <span style={s.pinnedStripIcon}>✓</span>
            <span style={s.pinnedStripText}>
              Submitting as <strong>{pinned.name}</strong>
              <span style={s.pinnedStripHint}> · This lead will be credited to you, time-stamped server-side.</span>
            </span>
          </div>
        )}
        {pinLoading && (
          <div style={{ padding: '14px 24px', color: LIGHT, fontSize: 13, background: '#FAF7F2', borderBottom: `1px solid ${BORDER}` }}>
            Loading share link…
          </div>
        )}
        {pinError && (
          <div style={{ padding: '14px 24px', background: '#FEF2F2', borderBottom: '1px solid #FCA5A5', color: '#991B1B', fontSize: 13, lineHeight: 1.6 }}>
            <strong>⚠ Invalid share link.</strong> {pinError}
          </div>
        )}

        {/* Two-column body: instructions left, form right.
            Collapses to single column under 1000px (see media query). */}
        <div className="lead-intake-grid" style={s.grid}>

          {/* ── LEFT: Instructions ── */}
          <aside style={s.colLeft}>
            <section style={s.steps}>
              <div style={s.stepsTitle}>Quick start</div>
              <ol style={s.stepsList}>
                <li>Find a distressed property on Zillow / Redfin / Realtor.</li>
                <li>Hit <kbd style={s.kbd}>⌘A</kbd> → <kbd style={s.kbd}>⌘C</kbd> on the listing page to copy everything.</li>
                <li>Fill the form on the right and submit.</li>
              </ol>
              <p style={{ margin: '10px 0 0', fontSize: 12, color: BURG_DK, fontStyle: 'italic' }}>
                Expand any section below for the full walk-through.
              </p>
            </section>
            <TrainingAccordion />
          </aside>

          {/* ── RIGHT: Form ── */}
          <section style={s.colRight}>
            <form ref={formRef} onSubmit={(e) => { e.preventDefault(); submit(); }} style={s.form}>

              {/* Lead Agent info — only on the truly generic public form
                  (no slug at all in URL). On Nicholas's / Denicia's
                  share links, the pinned strip up top covers attribution
                  and this section never renders. */}
              {!pinned && !pinLoading && !slug && (
                <fieldset style={s.section}>
                  <legend style={s.sectionLegend}>Lead Agent — your info</legend>
                  <p style={s.sectionHint}>So we know who to credit on the lead.</p>
                  <Field label="Your name" required>
                    <Input value={agent.name} onChange={e => setAgent({ ...agent, name: e.target.value })} placeholder="Jane Doe" autoComplete="name" />
                  </Field>
                  <Field label="Your phone" required>
                    <Input value={agent.phone} onChange={e => setAgent({ ...agent, phone: fmtPhone(e.target.value) })} placeholder="(555) 123-4567" inputMode="tel" autoComplete="tel" />
                  </Field>
                  <Field label="Your email" required hint="So we can keep you posted on this lead's status.">
                    <Input type="email" value={agent.email} onChange={e => setAgent({ ...agent, email: e.target.value })} placeholder="jane@example.com" autoComplete="email" />
                  </Field>
                </fieldset>
              )}

          {/* ── Property ── */}
          <fieldset style={s.section}>
            <legend style={s.sectionLegend}>Property details</legend>
            <Field label="Listing URL" required hint="Paste the full link from Zillow / Redfin / Realtor.">
              <Input type="url" value={listingUrl} onChange={e => setListingUrl(e.target.value)} placeholder="https://www.zillow.com/homedetails/..." />
            </Field>
            <Field label="Street address" required hint="Type to search — Google fills the rest.">
              <AddressAutocomplete
                value={address.line1}
                onChange={e => setAddress({ ...address, line1: e.target.value })}
                onSelect={(parts) => setAddress({
                  line1: parts.line1 || address.line1,
                  city: parts.city || address.city,
                  state: parts.state || address.state,
                  zip: parts.zip || address.zip
                })}
                placeholder="2847 Thornwood Lane"
              />
            </Field>
            <div style={s.row3}>
              <Field label="City"><Input value={address.city} onChange={e => setAddress({ ...address, city: e.target.value })} /></Field>
              <Field label="State"><Input value={address.state} onChange={e => setAddress({ ...address, state: e.target.value.toUpperCase().slice(0, 2) })} maxLength={2} /></Field>
              <Field label="Zip"><Input value={address.zip} onChange={e => setAddress({ ...address, zip: e.target.value })} /></Field>
            </div>
            <div style={s.row3small}>
              <Field label="Beds"><Input type="number" value={beds} onChange={e => setBeds(e.target.value)} placeholder="3" /></Field>
              <Field label="Baths"><Input type="number" step="0.5" value={baths} onChange={e => setBaths(e.target.value)} placeholder="2" /></Field>
              <Field label="Sq Ft (optional)"><Input type="number" value={sqft} onChange={e => setSqft(e.target.value)} placeholder="1450" /></Field>
            </div>
          </fieldset>

          {/* ── Owner ── */}
          <fieldset style={s.section}>
            <legend style={s.sectionLegend}>Owner / Seller</legend>
            <p style={s.sectionHint}>Grab the phone from the listing — usually under "Contact" or "Listed by." On Zillow FSBO listings it's right on the page. The owner's name often isn't listed — leave it blank if you don't have it.</p>
            <div style={s.row2}>
              <Field label="Owner phone" required>
                <Input value={ownerPhone} onChange={e => setOwnerPhone(fmtPhone(e.target.value))} placeholder="(555) 987-6543" inputMode="tel" />
              </Field>
              <Field label="Owner name" hint='Optional — leave blank if not shown on the listing.'>
                <Input value={ownerName} onChange={e => setOwnerName(e.target.value)} placeholder="Often not shown — that's fine" />
              </Field>
            </div>
          </fieldset>

          {err && <div style={s.err}>{err}</div>}

          <div style={s.actions}>
            <button type="submit" disabled={submitting} style={s.cta}>
              {submitting ? 'Submitting…' : 'Submit lead →'}
            </button>
          </div>

          <div style={s.foot}>
            By submitting you confirm the information you've provided is accurate. We'll only contact the seller — your details are kept private. © {new Date().getFullYear()} HomeIQa, LLC.
          </div>
            </form>
          </section>
        </div>

        {duplicate && (
          <DuplicateModal
            duplicate={duplicate}
            onClose={() => setDuplicate(null)}
            onProceed={() => { setDuplicate(null); submit(true); }}
          />
        )}
      </div>
    </div>
  );
}

// Comprehensive Lead Agent training — collapsible sections inline on
// the public intake page so anyone with a share link sees the full
// walk-through without needing to sign in. Mirrors the role-aware
// Training Guide that lives at /agent/training for signed-in agents,
// but stripped to what a Lead Agent needs.
function TrainingAccordion() {
  const [open, setOpen] = useState({ role: true });
  const toggle = (k) => setOpen(o => ({ ...o, [k]: !o[k] }));
  const sections = [
    {
      id: 'role',
      icon: '🎯',
      title: 'Your role',
      content: (
        <>
          <p style={t.p}>You're a <strong style={{ color: BURG_DK }}>Lead Agent</strong> — your job is research, organization, and accuracy.</p>
          <p style={t.p}><strong style={{ color: BURG_DK }}>You ARE responsible for:</strong></p>
          <ul style={t.ul}>
            <li>Searching for residential property listings</li>
            <li>Identifying properties that match company criteria</li>
            <li>Collecting property details</li>
            <li>Submitting the lead via this form</li>
          </ul>
          <p style={t.p}>Once you submit, the HomeIQa team takes over — owner contact, negotiation, contracts, and closing. You're credited as the Lead Agent on every property you find.</p>
        </>
      )
    },
    {
      id: 'areas',
      icon: '📍',
      title: 'How to pick a search area',
      content: (
        <>
          <p style={t.p}>Look for:</p>
          <ul style={t.ul}>
            <li>Mid-size cities (not the biggest metros, not the smallest towns)</li>
            <li>Older residential neighborhoods</li>
            <li>Areas with older homes</li>
            <li>Single-family home neighborhoods</li>
            <li>Areas with active home sales</li>
          </ul>
          <p style={t.p}><strong style={{ color: BURG_DK }}>Examples that work well:</strong></p>
          <ul style={t.ul}>
            <li>Jacksonville, Florida</li>
            <li>Fayetteville, North Carolina</li>
            <li>Memphis, Tennessee</li>
            <li>Birmingham, Alabama</li>
            <li>Indianapolis, Indiana</li>
            <li>Atlanta outskirts</li>
          </ul>
        </>
      )
    },
    {
      id: 'keywords',
      icon: '🔍',
      title: 'Where to find properties — Zillow keywords',
      content: (
        <>
          <p style={t.p}>Search residential listings for terms that suggest the property may need updates or repairs. These keywords help identify properties that meet our internal search guidelines:</p>
          <ul style={t.ul}>
            <li><code style={t.code}>As-Is</code></li>
            <li><code style={t.code}>Needs TLC</code></li>
            <li><code style={t.code}>Handyman special</code></li>
            <li><code style={t.code}>Needs work</code></li>
            <li><code style={t.code}>Estate sale</code></li>
            <li><code style={t.code}>Vacant</code></li>
            <li><code style={t.code}>Reduced price</code></li>
            <li><code style={t.code}>Distressed</code></li>
            <li><code style={t.code}>Cash only</code></li>
            <li><code style={t.code}>Investor special</code></li>
          </ul>
        </>
      )
    },
    {
      id: 'zillow',
      icon: '📝',
      title: 'How to search Zillow — step by step',
      content: (
        <>
          <ol style={t.ol}>
            <li><strong>Go to Zillow.com.</strong></li>
            <li><strong>Type in your assigned city.</strong> Example: <em>Jacksonville, Florida</em>.</li>
            <li>
              <strong>Expand the search area.</strong> When Zillow draws the blue border around the city:
              <ul style={t.ul}>
                <li>Click outside the blue border</li>
                <li>Expand the search radius</li>
                <li>Review surrounding neighborhoods</li>
              </ul>
            </li>
            <li>
              <strong>Apply filters:</strong>
              <ul style={t.ul}>
                <li>Houses only</li>
                <li>Single family homes</li>
                <li>Older homes</li>
                <li>Price reductions</li>
                <li>2–4 bedrooms</li>
              </ul>
            </li>
            <li>
              <strong>Review listing details carefully.</strong> Look for:
              <ul style={t.ul}>
                <li>Poor condition photos</li>
                <li>Outdated interiors</li>
                <li>Overgrown landscaping</li>
                <li>Damaged roofing</li>
                <li>Vacancy indicators</li>
                <li>Visible repair needs</li>
              </ul>
            </li>
          </ol>
        </>
      )
    },
    {
      id: 'collect',
      icon: '📋',
      title: 'What to collect',
      content: (
        <>
          <p style={t.p}><strong style={{ color: BURG_DK }}>Required for every submission:</strong></p>
          <ul style={t.ul}>
            <li>Property address</li>
            <li>Listing link (the Zillow URL)</li>
            <li>Owner's phone number (usually under "Contact" on FSBO listings)</li>
          </ul>
          <p style={t.p}><strong style={{ color: BURG_DK }}>Optional but very helpful:</strong></p>
          <ul style={t.ul}>
            <li>Bedrooms</li>
            <li>Bathrooms</li>
            <li>Square footage</li>
            <li>Year built</li>
            <li>Visible repairs / condition notes</li>
          </ul>
          <div style={t.tip}>
            💡 <strong>Tip:</strong> On Zillow FSBO listings, the owner's phone is usually right on the page under "Contact" or "Listed by." That's the gold mine.
          </div>
        </>
      )
    },
    {
      id: 'submit',
      icon: '🚀',
      title: 'Submit the lead',
      content: (
        <>
          <ol style={t.ol}>
            <li>On the Zillow listing, hit <kbd style={t.kbdInline}>⌘A</kbd> (Mac) or <kbd style={t.kbdInline}>Ctrl+A</kbd> (Windows) → <kbd style={t.kbdInline}>⌘C</kbd> / <kbd style={t.kbdInline}>Ctrl+C</kbd> to copy everything.</li>
            <li>Scroll to the form below.</li>
            <li>Type the property address (Google autofills city/state/zip).</li>
            <li>Paste the listing URL.</li>
            <li>Type the owner's phone.</li>
            <li>Click <strong>Submit Lead →</strong>.</li>
          </ol>
          <div style={t.tip}>
            💡 You're credited as the <strong>Lead Agent</strong> on every property you find. Your name and the timestamp are saved on the lead permanently.
          </div>
        </>
      )
    },
    {
      id: 'after',
      icon: '🤝',
      title: 'What happens after you submit',
      content: (
        <>
          <ul style={t.ul}>
            <li>The lead lands in the HomeIQa pipeline with your name and timestamp.</li>
            <li>The HomeIQa team reaches out to the seller, usually within 24 hours.</li>
            <li>You don't need to follow up with the seller — that's the team's job.</li>
            <li>If we close the deal, you'll know because the lead's status moves through Contacted → Negotiating → Under Contract → Assigned → Closed.</li>
          </ul>
        </>
      )
    }
  ];

  return (
    <section style={t.wrap}>
      <div style={t.heading}>📖 Full walk-through — how to find leads</div>
      <div style={t.cards}>
        {sections.map(sec => (
          <div key={sec.id} style={t.card}>
            <button onClick={() => toggle(sec.id)} style={t.head}>
              <span style={t.headLeft}>
                <span style={t.icon}>{sec.icon}</span>
                <span style={t.headTitle}>{sec.title}</span>
              </span>
              <span style={{ ...t.chevron, transform: open[sec.id] ? 'rotate(90deg)' : 'rotate(0)' }}>›</span>
            </button>
            {open[sec.id] && <div style={t.body}>{sec.content}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}

const t = {
  wrap: { padding: '20px 0 0', background: 'transparent' },
  heading: { fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 22, fontWeight: 500, color: BURG_DK, marginBottom: 14, letterSpacing: '-0.3px' },
  cards: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: { border: '1px solid rgba(107,30,46,0.15)', borderRadius: 10, overflow: 'hidden', background: '#fff' },
  head: { width: '100%', background: 'transparent', border: 'none', padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", textAlign: 'left' },
  headLeft: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 },
  icon: { fontSize: 20, flexShrink: 0 },
  headTitle: { fontSize: 16, fontWeight: 500, color: BURG_DK, lineHeight: 1.3 },
  chevron: { fontSize: 22, color: GOLD, transition: 'transform 0.2s ease', width: 14, textAlign: 'center', flexShrink: 0 },
  body: { padding: '4px 18px 18px', borderTop: '1px solid rgba(107,30,46,0.08)', background: '#FAF7F2' },
  p: { fontSize: 14, color: '#1C1C1C', lineHeight: 1.65, margin: '12px 0' },
  ul: { paddingLeft: 22, margin: '8px 0', fontSize: 14, color: '#1C1C1C', lineHeight: 1.75 },
  ol: { paddingLeft: 22, margin: '8px 0', fontSize: 14, color: '#1C1C1C', lineHeight: 1.75 },
  code: { background: '#fff', padding: '2px 8px', borderRadius: 4, border: '1px solid rgba(107,30,46,0.15)', fontSize: 13, color: BURG, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
  tip: { background: GOLD_PALE, border: `1px solid rgba(201,168,76,0.4)`, borderLeft: `3px solid ${GOLD}`, padding: '10px 14px', fontSize: 13, color: BURG_DK, lineHeight: 1.6, borderRadius: '0 8px 8px 0', margin: '12px 0 4px' },
  kbdInline: { display: 'inline-block', background: '#fff', border: '1px solid rgba(107,30,46,0.18)', borderRadius: 4, padding: '1px 7px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, color: BURG }
};

function Field({ label, children, hint, required }) {
  return (
    <label style={s.field}>
      <span style={s.label}>{label}{required && <span style={{ color: BURG, marginLeft: 3 }}>*</span>}</span>
      {children}
      {hint && <span style={s.hint}>{hint}</span>}
    </label>
  );
}

function Input(props) {
  return <input {...props} style={{ ...s.input, ...(props.style || {}) }} />;
}

function DuplicateModal({ duplicate, onClose, onProceed }) {
  const fullAddr = [duplicate.address?.line1, duplicate.address?.city, duplicate.address?.state, duplicate.address?.zip].filter(Boolean).join(', ');
  return (
    <div style={mod.backdrop} onClick={onClose}>
      <div style={mod.panel} onClick={e => e.stopPropagation()}>
        <div style={mod.head}>
          <div style={mod.eyebrow}>⚠ Already in the pipeline</div>
        </div>
        <div style={mod.body}>
          <p style={{ margin: '0 0 12px', color: BURG_DK }}>
            This property is already in the HomeIQa pipeline (matched on <strong>{duplicate.matchedOn}</strong>).
          </p>
          <div style={mod.meta}>
            <div><strong>Address:</strong> {fullAddr || '—'}</div>
            <div><strong>Status:</strong> {duplicate.status || 'New'}</div>
            {duplicate.submittedByName && <div><strong>Originally by:</strong> {duplicate.submittedByName}</div>}
          </div>
          <p style={{ margin: '12px 0 0', color: MID, fontSize: 13 }}>
            We won't add a duplicate. If you're sure this is a different property at the same address, you can submit anyway.
          </p>
        </div>
        <div style={mod.footer}>
          <button onClick={onClose} style={s.btnGhost}>OK, cancel</button>
          <button onClick={onProceed} style={s.btnDanger}>Submit anyway</button>
        </div>
      </div>
    </div>
  );
}

const s = {
  shell: { minHeight: '100vh', background: CREAM, fontFamily: "'DM Sans', Helvetica, Arial, sans-serif", padding: '40px 20px', color: '#1C1C1C' },
  wrap: { maxWidth: 1100, margin: '0 auto', background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, overflow: 'hidden', boxShadow: '0 24px 64px rgba(74,18,32,0.08)' },

  // Thin identity strip just below the header — Nicholas / Denicia /
  // anyone using a personalized link sees their name here, NEVER in
  // the middle of the page or in the form column.
  pinnedStrip: {
    display: 'flex', alignItems: 'center', gap: 12,
    background: BURG_DK, color: '#fff',
    borderBottom: `2px solid ${GOLD}`,
    padding: '12px 28px', fontSize: 14, lineHeight: 1.4
  },
  pinnedStripIcon: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 24, height: 24, borderRadius: '50%',
    background: GOLD, color: BURG_DK, fontWeight: 700, fontSize: 14, flexShrink: 0
  },
  pinnedStripText: { color: '#fff' },
  pinnedStripHint: { color: 'rgba(255,255,255,0.7)' },

  // Two-column grid: instructions left (collapsible) / form right.
  // Single-column on screens narrower than 1000px (set via injected
  // <style> at module load — see CSS injection at bottom of file).
  grid: { display: 'grid', gridTemplateColumns: '1fr', gap: 0 },
  colLeft: { padding: '28px 28px 32px', background: CREAM, borderRight: `1px solid ${BORDER}` },
  colRight: { padding: '28px 28px 32px' },
  header: { background: BURG_DK, color: '#fff', padding: '32px 32px 28px', borderBottom: `2px solid ${GOLD}` },
  brand: { fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 26, fontWeight: 600, color: '#fff', letterSpacing: '-0.5px' },
  eyebrow: { fontSize: 10, fontWeight: 500, letterSpacing: '0.18em', textTransform: 'uppercase', color: GOLD, marginTop: 18, marginBottom: 6 },
  title: { fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 30, fontWeight: 500, color: '#fff', margin: '0 0 10px', letterSpacing: '-0.4px', lineHeight: 1.15 },
  sub: { fontSize: 14, color: 'rgba(255,255,255,0.78)', lineHeight: 1.6, margin: 0 },

  steps: { padding: '16px 18px', background: GOLD_PALE, borderRadius: 10, border: `1px solid rgba(201,168,76,0.4)` },
  stepsTitle: { fontSize: 11, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: BURG, marginBottom: 12 },
  stepsList: { paddingLeft: 22, margin: 0, fontSize: 13, color: '#1C1C1C', lineHeight: 1.7 },

  form: { padding: 0, display: 'flex', flexDirection: 'column', gap: 18 },
  section: { border: `1px solid ${BORDER}`, borderRadius: 10, padding: '18px 20px 6px', margin: 0 },
  sectionLegend: { fontSize: 10, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: BURG, padding: '0 8px' },
  sectionHint: { margin: '0 0 14px', fontSize: 12, color: MID, lineHeight: 1.5 },

  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  row3: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 },
  row3small: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 },

  field: { display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 },
  label: { fontSize: 11, fontWeight: 500, color: MID, letterSpacing: '0.04em', textTransform: 'uppercase' },
  hint: { fontSize: 11, color: LIGHT, lineHeight: 1.5 },
  input: { padding: '12px 14px', border: `1px solid ${BORDER}`, borderRadius: 10, fontSize: 14, fontFamily: "'DM Sans', sans-serif", background: '#fff', color: '#1C1C1C', outline: 'none', transition: 'border-color 0.15s ease' },

  err: { padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, color: '#991B1B', fontSize: 13, lineHeight: 1.5 },

  actions: { display: 'flex', justifyContent: 'flex-end', marginTop: 8 },
  cta: { background: GOLD, color: BURG_DK, border: 'none', borderRadius: 10, padding: '14px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.02em' },
  foot: { fontSize: 11, color: LIGHT, lineHeight: 1.6, textAlign: 'center', paddingTop: 14, borderTop: `1px solid ${BORDER}`, marginTop: 4 },

  pinned: { background: BURG_DK, color: '#fff', borderRadius: 12, padding: '18px 22px', border: `1px solid ${GOLD}`, marginBottom: 4 },
  pinnedEyebrow: { fontSize: 10, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD_PALE, marginBottom: 6 },
  pinnedName: { fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 22, fontWeight: 500, color: '#fff', lineHeight: 1.2, marginBottom: 6 },
  pinnedHint: { fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.55 },

  successCard: { maxWidth: 560, margin: '40px auto', background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, padding: '40px 36px', textAlign: 'center', boxShadow: '0 24px 64px rgba(74,18,32,0.08)' },
  successIcon: { width: 64, height: 64, borderRadius: '50%', background: '#E7F5EE', color: '#0F6B3F', fontSize: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontWeight: 700 },
  successTitle: { fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 28, fontWeight: 500, color: BURG_DK, margin: '0 0 12px', letterSpacing: '-0.3px' },
  successBody: { fontSize: 14, color: MID, lineHeight: 1.65, margin: '0 0 22px' },
  successMeta: { background: CREAM, border: `1px solid ${BORDER}`, borderRadius: 10, padding: '16px 18px', fontSize: 13, color: '#1C1C1C', textAlign: 'left', lineHeight: 1.8 },
  again: { marginTop: 22, background: BURG_DK, color: '#fff', border: 'none', borderRadius: 10, padding: '12px 24px', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },

  btnGhost: { background: 'transparent', color: BURG, border: `1px solid ${BORDER}`, borderRadius: 10, padding: '10px 18px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  btnDanger: { background: '#fff', color: '#991B1B', border: '1px solid #FCA5A5', borderRadius: 10, padding: '10px 18px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }
};

const mod = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(28,28,28,0.55)', backdropFilter: 'blur(2px)', zIndex: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  panel: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 480, boxShadow: '0 24px 64px rgba(0,0,0,0.3)', overflow: 'hidden', fontFamily: "'DM Sans', sans-serif" },
  head: { padding: '14px 22px', background: '#FEF3C7', borderBottom: '1px solid #FCD34D' },
  eyebrow: { fontSize: 12, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#92400E' },
  body: { padding: '20px 22px' },
  meta: { background: CREAM, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '12px 14px', fontSize: 12, color: '#1C1C1C', lineHeight: 1.7 },
  footer: { padding: '14px 22px', borderTop: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }
};

// Responsive grid: instructions + form side-by-side on desktop,
// stacked on tablet / mobile. Inline styles can't do media queries,
// so we inject a single <style> tag once.
if (typeof document !== 'undefined' && !document.getElementById('homeiqa-lead-intake-css')) {
  const tag = document.createElement('style');
  tag.id = 'homeiqa-lead-intake-css';
  tag.textContent = `
    @media (min-width: 1000px) {
      .lead-intake-grid { grid-template-columns: 360px 1fr !important; }
    }
    @media (max-width: 999px) {
      .lead-intake-grid > aside { border-right: none !important; border-bottom: 1px solid rgba(107,30,46,0.18); }
    }
  `;
  document.head.appendChild(tag);
}
